import React, { useState, useMemo } from 'react';
import { Search, PlusCircle, MinusCircle, RefreshCw } from 'lucide-react';
import { type StudentWallet, formatMoney } from '../../../lib/apis_vibe';

interface WalletListTabProps {
  students: StudentWallet[];
  loading: boolean;
  currentSchoolName: string;
  onBatchRecharge: () => void;
  onSingleRecharge: (student: StudentWallet) => void;
  onSingleDeduct: (student: StudentWallet) => void;
  onViewLogs: (student: StudentWallet) => void;
  roles: string[];
  memos: string[];
  selectedRole: string;
  selectedMemo: string;
  onRoleChange: (role: string) => void;
  onMemoChange: (memo: string) => void;
}

const WalletListTab: React.FC<WalletListTabProps> = ({
  students,
  loading,
  currentSchoolName,
  onBatchRecharge,
  onSingleRecharge,
  onSingleDeduct,
  onViewLogs,
  roles,
  memos,
  selectedRole,
  selectedMemo,
  onRoleChange,
  onMemoChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredStudents = useMemo(() => {
    if (!searchTerm) return students;
    const lower = searchTerm.toLowerCase();
    return students.filter(s => {
      const nameMatch = s.username ? s.username.toLowerCase().includes(lower) : false;
      const idMatch = (s.user_id !== undefined && s.user_id !== null) ? s.user_id.toString().includes(lower) : false;
      return nameMatch || idMatch;
    });
  }, [students, searchTerm]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* 工具栏 */}
      <div className="p-5 border-b border-gray-100 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">学员列表</h2>
            <p className="text-xs text-gray-500 mt-1">{currentSchoolName}</p>
          </div>
          
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="搜索用户名或ID..." 
                className="pl-10 pr-4 py-2 border rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <button 
              onClick={onBatchRecharge}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition shadow-sm active:scale-95"
            >
              <PlusCircle size={18} /> 全校批量充值
            </button>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">角色筛选:</label>
            <select
              value={selectedRole}
              onChange={e => onRoleChange(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none min-w-[120px]"
            >
              <option value="">全部角色</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">备注筛选:</label>
            <select
              value={selectedMemo}
              onChange={e => onMemoChange(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none min-w-[150px]"
            >
              <option value="">全部备注</option>
              {memos.map(memo => (
                <option key={memo} value={memo}>{memo}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 表格 */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600 font-semibold border-b">
            <tr>
              <th className="px-6 py-4">学员信息</th>
              <th className="px-6 py-4">当前余额</th>
              <th className="px-6 py-4">今日消耗</th>
              <th className="px-6 py-4">昨日消耗</th>
              <th className="px-6 py-4">前日消耗</th>
              <th className="px-6 py-4">历史消耗</th>
              <th className="px-6 py-4 text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="p-10 text-center text-gray-500">
                  <div className="flex justify-center items-center gap-2">
                    <RefreshCw className="animate-spin" size={20} /> 数据加载中...
                  </div>
                </td>
              </tr>
            ) : filteredStudents.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center text-gray-400">暂无数据</td></tr>
            ) : (
              filteredStudents.map(s => (
                <tr key={s.user_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{s.username}</div>
                    <div className="text-xs text-gray-400 font-mono">ID: {s.user_id}</div>
                    <div className="flex gap-2 mt-1">
                      <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{s.role || '学员'}</span>
                      {s.memo && <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded truncate max-w-[120px]" title={s.memo}>{s.memo}</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-bold ${s.balance < 100 ? 'text-red-500' : 'text-green-600'}`}>
                      {s.balance.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-orange-600">
                    <div>{s.usage_today?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{formatMoney(s.usage_today_money)}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    <div>{s.usage_yesterday?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{formatMoney(s.usage_yesterday_money)}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    <div>{s.usage_day_before_yesterday?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{formatMoney(s.usage_day_before_yesterday_money)}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    <div>{s.total_used.toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{formatMoney(s.used_money)}</div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => onSingleRecharge(s)} 
                        className="px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        充值
                      </button>
                      <button 
                        onClick={() => onSingleDeduct(s)} 
                        className="px-3 py-1.5 border border-red-300 rounded text-red-600 hover:bg-red-50 transition-colors"
                      >
                        扣减
                      </button>
                      <button 
                        onClick={() => onViewLogs(s)} 
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                      >
                        明细
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WalletListTab;
